import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { useSwipeBack } from '@/hooks'
import { useTgBootstrap } from './TgBootstrap'
import { getPlatform } from '@shared/platform'
import { ROUTES } from '@shared/config/routes'
import { useAppStore } from '@/store/useAppStore'
import { LinkEmailAutoPromptTg } from '@features/link-email/ui/LinkEmailAutoPromptTg'
import { LinkTelegramConfirmScreen } from '@features/link-telegram/ui/LinkTelegramConfirmScreen'
import { TelegramAuthConfirmScreen } from '@features/telegram-auth'
import {
  useCategories,
  useCards,
  useFavorites,
  useAnnouncements,
  useSubscription,
} from '@/hooks/useApi'
import LoadingScreen from '@/components/LoadingScreen/LoadingScreen'
import FortuneLoadingScreen from '@/components/FortuneLoadingScreen/FortuneLoadingScreen'
import ScrollToTop from '@/components/ScrollToTop'
import Layout from '@/components/Layout/Layout'
import HomePage from '@/pages/HomePage/HomePage'
import ProfilePage from '@/pages/ProfilePage/ProfilePage'
import CardPage from '@/pages/CardPage'
import TestVideoPage from '@/pages/TestVideoPage'
import SubscribePage from '@/pages/SubscribePage'
import CoursesPage from '@/pages/CoursesPage/CoursesPage'
import CourseDetailPage from '@/pages/CourseDetailPage/CourseDetailPage'
import LessonPage from '@/pages/LessonPage/LessonPage'
import FavoritesPage from '@/pages/FavoritesPage/FavoritesPage'
import FlavorsPage from '@/pages/FlavorsPage/FlavorsPage'
import CalculatorPage from '@/pages/CalculatorPage/CalculatorPage'
import ShapeCalculatorPage from '@/pages/ShapeCalculatorPage/ShapeCalculatorPage'
import CostCalculatorPage from '@/pages/CostCalculatorPage/CostCalculatorPage'
import AssistantPage from '@/pages/AssistantPage/AssistantPage'
import FortuneWheelPage from '@/pages/FortuneWheelPage/FortuneWheelPage'
import TermsPage from '@pages/TermsPage/TermsPage'
import PrivacyPage from '@pages/PrivacyPage/PrivacyPage'
import { PaymentStatusModal } from '@features/payments'
import { NpsSurveyHost } from '@features/nps-survey'
import { GracePeriodPopup } from '@/pages/ProfilePage/components/SubscriptionSection/GracePeriodPopup'
import { FEATURES } from '@/config/features'

export const TgAppShell = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isLoading, startParam } = useTgBootstrap()
  const { isLoading: storeLoading, authStatus } = useAppStore()
  const [showInitialLoading, setShowInitialLoading] = useState(true)

  const startParamHandledRef = useRef(false)
  useEffect(() => {
    // Параметр берём и у платформы: хук отдаёт его только после своей
    // инициализации, а она пропускается, если сессия уже поднята — на «тёплом»
    // старте deep link иначе теряется.
    const param = startParam || getPlatform().getStartParam()
    if (!param) return
    if (startParamHandledRef.current) return
    startParamHandledRef.current = true

    if (param.startsWith('link_code_')) {
      navigate(`/link-telegram/confirm?token=${encodeURIComponent(param)}`, { replace: true })
    } else if (param.startsWith('telegram_auth_')) {
      navigate(`/telegram-auth/confirm?token=${encodeURIComponent(param)}`, { replace: true })
    } else if (param === 'flavors') {
      navigate(ROUTES.FLAVORS, { replace: true })
    } else {
      // Незнакомый параметр — не наш сценарий, оставляем главную.
      startParamHandledRef.current = false
    }
  }, [startParam, navigate])

  const isAuthComplete = authStatus === 'success'
  useCategories(isAuthComplete)
  useCards()
  useFavorites()
  useAnnouncements()
  useSubscription()

  useEffect(() => {
    const timer = setTimeout(() => setShowInitialLoading(false), 3500)
    return () => clearTimeout(timer)
  }, [])

  const handleLoadingComplete = () => setShowInitialLoading(false)

  const shouldShowLoading = showInitialLoading || isLoading || storeLoading
  const isFortuneWheelRoute =
    FEATURES.fortuneWheel &&
    (startParam === 'wheel' || location.pathname.startsWith('/fortune-wheel'))
  // Возврат назад свайпом от левого края — только в Telegram: в браузере такой
  // жест уже системный, и наш дублировал бы его, уводя на две записи назад.
  // idx в history.state ведёт react-router: 0 — первая запись, назад некуда.
  const canGoBack = ((window.history.state?.idx as number | undefined) ?? 0) > 0
  const contentRef = useRef<HTMLDivElement>(null)
  useSwipeBack({
    enabled: canGoBack,
    onBack: () => navigate(-1),
    targetRef: contentRef,
  })

  const shouldApplySafeArea =
    !location.pathname.startsWith('/card/') &&
    !location.pathname.startsWith('/subscribe') &&
    !location.pathname.match(/^\/courses\/\d/)

  return (
    <div className={shouldApplySafeArea ? 'safe-area-inset' : ''}>
      <div
        ref={contentRef}
        style={{ visibility: shouldShowLoading ? 'hidden' : 'visible' }}
      >
        <ScrollToTop />
        <Routes>
          <Route path="card/:cardId" element={<CardPage />} />
          <Route path="courses/:courseId" element={<CourseDetailPage />} />
          <Route path="courses/:courseId/lesson/:lessonId" element={<LessonPage />} />
          <Route path="test-video" element={<TestVideoPage />} />
          <Route path="subscribe" element={<SubscribePage />} />
          <Route path="calculator/:cardId?" element={<CalculatorPage />} />
          <Route
            path="fortune-wheel"
            element={
              FEATURES.fortuneWheel ? <FortuneWheelPage /> : <Navigate to="/" replace />
            }
          />
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="link-telegram/confirm" element={<LinkTelegramConfirmScreen />} />
          <Route path="telegram-auth/confirm" element={<TelegramAuthConfirmScreen />} />
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="courses" element={<CoursesPage />} />
            <Route path="favorites" element={<FavoritesPage />} />
            <Route path="flavors" element={<FlavorsPage />} />
            <Route path="cost-calculator" element={<CostCalculatorPage />} />
            <Route path="shape-calculator" element={<ShapeCalculatorPage />} />
            <Route path="assistant" element={<AssistantPage />} />
          </Route>
        </Routes>
      </div>

      <LinkEmailAutoPromptTg />
      <NpsSurveyHost />
      <PaymentStatusModal />
      <GracePeriodPopup />

      {shouldShowLoading &&
        (isFortuneWheelRoute ? (
          <FortuneLoadingScreen
            isLoading={true}
            onComplete={showInitialLoading ? handleLoadingComplete : undefined}
          />
        ) : (
          <LoadingScreen
            isLoading={true}
            onComplete={showInitialLoading ? handleLoadingComplete : undefined}
          />
        ))}
    </div>
  )
}
